import React, { useMemo } from "react";
import { useRemoteSensors } from "@/hooks/useRemoteSensors";
import { t } from "@lingui/macro";
import { MenuList, MenuListItemVariant, ChevronRightIcon } from "@WahooFitness/redesignr";
import { Box, useTheme } from "@mui/material";
import { useUnitFormatter } from "@/hooks/useUnitFormatter";
import { useNavigate } from "react-router";
import { crux_sensor_product_type_e } from "@WahooFitness/crux-js-display-cfg";
import WSMSensorProductType from "@WahooFitness/wsm-native/dist/esm/types/product_type";

interface WheelCircumferenceConfigProps {
  appToken: string;
  remoteSensorId: number;
}

const WheelCircumferenceConfig: React.FC<WheelCircumferenceConfigProps> = ({
  appToken,
  remoteSensorId,
}: WheelCircumferenceConfigProps) => {
  const { remoteSensorStatus } = useRemoteSensors(appToken);
  const { palette } = useTheme();
  const { alwaysMetricUnitFormatter } = useUnitFormatter();
  const navigate = useNavigate();

  const remoteSensor = remoteSensorStatus?.sensors?.find(
    (sensor) => sensor?.cfg?.sensor_id === remoteSensorId
  );

  const shouldHideForSensor = useMemo(
    () =>
      [
        crux_sensor_product_type_e.WAHOO_KICKR_BIKE,
        crux_sensor_product_type_e.WAHOO_KICKR_BIKE_SHIFT,
        crux_sensor_product_type_e.WAHOO_KICKR_ROLLR,
        crux_sensor_product_type_e.WAHOO_KICKR_SNAP,
        crux_sensor_product_type_e.GENERIC_BIKE_CADENCE,
        crux_sensor_product_type_e.GENERIC_LEV,
      ].includes(remoteSensor?.status.sensor_product_type as WSMSensorProductType),
    [remoteSensor?.status.sensor_product_type]
  );

  if (!remoteSensorStatus) {
    return null;
  }

  const wheelCircumferenceInM = remoteSensor?.cfg?.wheel_circ_mm
    ? (remoteSensor?.cfg?.wheel_circ_mm / 1000.0).toFixed(3)
    : 0;

  const units = alwaysMetricUnitFormatter?.formatElevation(wheelCircumferenceInM).formattedUnit;

  return (
    <MenuList
      listItems={[
        {
          id: "wheel_circumference_config",
          content: t`Wheel Circumference`,
          secondaryContent: remoteSensor?.cfg?.is_wheel_circ_auto_calib_enabled
            ? `Automatic`
            : `${wheelCircumferenceInM}${units}`,
          disabled: remoteSensor?.cfg?.wheel_circ_mm === undefined,
          variant: MenuListItemVariant.Action,
          hidden: shouldHideForSensor,
          actionComponent: (
            <Box display="flex" flexDirection="row" gap={1}>
              <ChevronRightIcon sx={{ color: palette.action.active }} />
            </Box>
          ),
          action: () =>
            navigate(
              `/device-config/${appToken}/sensor/${remoteSensor?.remote_sensor_id[0]}/wheel-circumference`
            ),
        },
      ]}
      disableGutters
    />
  );
};

export { WheelCircumferenceConfig };
